import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, PlusCircle, MapPin, Calendar, Edit, MessageSquare, Trash2, Search, Plus, Minus, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import { Report, ReportType, ProjectDetails, ProjectWorkflowStatus } from '../../types';
import { EmptyState } from '../../components/common/EmptyState';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { API_BASE_URL } from '../../config';
import { ProjectReportCard } from '../../components/admin/ProjectReportCard';
import ModernProjectDashboardScreen from './ModernProjectDashboardScreen';

// Helper function for status badges, consistent with other screens
const getStatusBadge = (status?: ProjectWorkflowStatus, t?: (key: string) => string) => {
    switch (status) {
        case ProjectWorkflowStatus.PendingTeamAcceptance:
            return <Badge variant="warning">{t ? t('projectStatusPendingTeamAcceptance') : 'بانتظار موافقة الفريق'}</Badge>;
        case ProjectWorkflowStatus.InProgress:
            return <Badge variant="default" className="bg-blue-500/10 text-blue-500">{t ? t('projectStatusInProgress') : 'قيد التنفيذ'}</Badge>;
        case ProjectWorkflowStatus.FinishingWorks:
            return <Badge variant="default" className="bg-indigo-500/10 text-indigo-500">{t ? t('projectStatusFinishingWorks') : 'أعمال التشطيبات'}</Badge>;
        case ProjectWorkflowStatus.ConcreteWorksDone:
            return <Badge variant="default" className="bg-orange-500/10 text-orange-500">{t ? t('projectStatusAwaitingSecondPayment') : 'بانتظار الدفعة الثانية'}</Badge>;
        case ProjectWorkflowStatus.TechnicallyCompleted:
            return <Badge variant="success">{t ? t('projectStatusTechnicallyCompleted') : 'مكتمل فنياً'}</Badge>;
        case ProjectWorkflowStatus.Finalized:
            return <Badge variant="success">{t ? t('projectStatusFinalized') : 'مكتمل'}</Badge>;
        default:
            return <Badge>{t ? t('projectStatusDraft') : 'مسودة'}</Badge>;
    }
}

const getStageLabelById = (id: string, t: (key: string) => string) => {
    switch (id) {
        case 'contract':
            return t('projectStageContract');
        case 'firstPayment':
            return t('projectStageFirstPayment');
        case 'notifyTeam':
            return t('projectStageNotifyTeam');
        case 'concreteWorks':
            return t('projectStageConcreteWorks');
        case 'secondPayment':
            return t('projectStageSecondPayment');
        case 'installationComplete':
            return t('projectStageInstallationComplete');
        case 'deliveryHandover':
            return t('projectStageDeliveryHandover');
        default:
            return id;
    }
};

const getCurrentStageLabel = (details: ProjectDetails, t: (key: string) => string): string => {
    const reversedUpdates = [...details.updates].reverse();
    const lastCompletedUpdate = reversedUpdates.find(u => u.completed);
    if (lastCompletedUpdate) {
        return `${t('projectCurrentStagePrefix')} ${getStageLabelById(lastCompletedUpdate.id, t)}`;
    }
    return t('projectStagesNotStarted');
};

const ProjectCard: React.FC<{ project: Report }> = ({ project }) => {
    const navigate = useNavigate();
    const { t, user } = useAppContext();
    const details = project.details as ProjectDetails;
    const [isHovered, setIsHovered] = React.useState(false);

    const hasUnreadNotes = useMemo(() => {
        if (!project.adminNotes || !user) return false;
        for (const note of project.adminNotes) {
            if (!note.readBy?.includes(user.id)) return true;
            if (note.replies) {
                for (const reply of note.replies) {
                    if (!reply.readBy?.includes(user.id)) return true;
                }
            }
        }
        return false;
    }, [project.adminNotes, user]);


    return (
        <Card
            className="cursor-pointer flex flex-col"
            onClick={() => navigate(`/reports/${project.id}`)}
        >
            <CardHeader>
                <div className="flex justify-between items-start">
                    <div>
                        <CardTitle className="mb-1 truncate">{details.projectOwner}</CardTitle>
                        <p className="text-xs text-slate-500 font-mono">{project.id}</p>
                    </div>
                    {getStatusBadge(project.projectWorkflowStatus, t)}
                </div>
            </CardHeader>
            <CardContent className="flex-1 space-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                    <MapPin size={14} />
                    <span>{details.location}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                    <Calendar size={14} />
                    <span>{t('startDate')}: {new Date(details.startDate).toLocaleDateString('ar-SA')}</span>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                    <p className="text-xs font-semibold text-primary">{getCurrentStageLabel(details, t)}</p>
                </div>
            </CardContent>

        </Card>
    );
};

const ClassicProjectDashboardScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports } = useAppStore();
    const navigate = useNavigate();
    interface SavedQuotationSummary {
        id: string;
        quote_number: string;
        quote_date: string;
        customer_name: string;
        location: string | null;
        mobile: string | null;
        created_at: string;
        total_with_tax: number;
    }
    const [savedQuotations, setSavedQuotations] = useState<SavedQuotationSummary[]>([]);
    useEffect(() => {
        const fetchSaved = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/quotations?limit=12`);
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data)) setSavedQuotations(data as SavedQuotationSummary[]);
                else if (Array.isArray((data as any).items)) setSavedQuotations((data as any).items as SavedQuotationSummary[]);
            } catch { }
        };
        fetchSaved();
    }, []);

    const deleteQuotationCard = async (id: string) => {
        try {
            const ok = confirm('هل تريد حذف العرض؟');
            if (!ok) return;
            const res = await fetch(`${API_BASE_URL}/quotations/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(await res.text());
            setSavedQuotations(prev => prev.filter(q => q.id !== id));
        } catch (e) {
            alert('تعذر حذف العرض');
        }
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | ProjectWorkflowStatus>('all');
    const projectReports = React.useMemo(() => {
        if (!user) return [];
        return reports
            .filter(r => r.type === ReportType.Project && r.employeeId === user.employeeId)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [reports, user]);
    const filteredReports = React.useMemo(() => {
        const searchLower = searchTerm.toLowerCase();
        return projectReports.filter(report => {
            const details = report.details as ProjectDetails;
            const searchMatch = searchLower === '' ||
                (report.employeeName || '').toLowerCase().includes(searchLower) ||
                (details.projectOwner || '').toLowerCase().includes(searchLower) ||
                (report.id || '').toLowerCase().includes(searchLower);
            const statusMatch = statusFilter === 'all' || report.projectWorkflowStatus === statusFilter;
            return searchMatch && statusMatch;
        });
    }, [projectReports, searchTerm, statusFilter]);

    const [projectsExpanded, setProjectsExpanded] = useState(false);
    const [quotationsExpanded, setQuotationsExpanded] = useState(false);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [quotationDetails, setQuotationDetails] = useState<Record<string, { horsepower?: number; device_type?: string; device_count?: number }>>({});

    useEffect(() => {
        if (!quotationsExpanded || savedQuotations.length === 0) return;
        const missing = savedQuotations.filter(q => !quotationDetails[q.id]);
        if (missing.length === 0) return;
        (async () => {
            try {
                const results = await Promise.all(missing.map(async (q) => {
                    const res = await fetch(`${API_BASE_URL}/quotations/${q.id}`);
                    if (!res.ok) return { id: q.id, items: [] } as any;
                    const data = await res.json();
                    const first = Array.isArray(data.items) && data.items.length > 0 ? data.items[0] : null;
                    return { id: q.id, first } as { id: string, first: any };
                }));
                setQuotationDetails(prev => {
                    const next = { ...prev };
                    for (const r of results) {
                        if (r.first) {
                            next[r.id] = {
                                horsepower: Number(r.first.horsepower || 0),
                                device_type: r.first.device_type,
                                device_count: r.first.device_count,
                            };
                        } else {
                            next[r.id] = {};
                        }
                    }
                    return next;
                });
            } catch { }
        })();
    }, [quotationsExpanded, savedQuotations]);

    return (
        <div className="space-y-6">
            <ScreenHeader
                icon={Briefcase}
                title={t('projectReports')}
                colorClass="bg-nav-project"
                onBack="/"
                actionButton={
                    <div className="flex items-center gap-2">
                        <button className="button" onClick={() => setFiltersCollapsed(v => !v)} aria-label={filtersCollapsed ? 'فتح الفلاتر' : 'طيّ الفلاتر'}>
                            <span className="button-box">
                                {filtersCollapsed ? <ChevronUp size={20} className="button-elem" /> : <ChevronDown size={20} className="button-elem" />}
                                {filtersCollapsed ? <ChevronDown size={20} className="button-elem" /> : <ChevronUp size={20} className="button-elem" />}
                            </span>
                        </button>
                        <Button
                            variant="secondary"
                            onClick={() => navigate('/quotations/new')}
                            className="hidden sm:inline-flex"
                        >
                            {t('createQuotation')}
                        </Button>
                        <Button
                            icon={<PlusCircle size={18} />}
                            onClick={() => navigate('/projects/new')}
                        >
                            {t('newProjectReport')}
                        </Button>
                    </div>
                }
            />
            <style>{`
            .button { display:block; position:relative; width:56px; height:56px; margin:0; overflow:hidden; outline:none; background-color:transparent; cursor:pointer; border:0; }
            .button-box { display:flex; flex-direction:column; position:absolute; top:0; left:0; }
            .button-elem { display:block; width:20px; height:20px; margin:17px 18px 0 18px; }
            .button:hover .button-box, .button:focus .button-box { transition:.4s; transform:translateY(-56px); }
            `}</style>

            {!filtersCollapsed && (
                <Card>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <Input
                                    placeholder="بحث باسم الموظف, مالك المشروع, أو رقم التقرير..."
                                    icon={<Search size={16} />}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <select
                                className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 py-2 px-3 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value as any)}
                            >
                                <option value="all">كل الحالات</option>
                                {Object.entries(ProjectWorkflowStatus).map(([key, value]) => (
                                    <option key={key} value={value}>{value}</option>
                                ))}
                            </select>
                        </div>
                    </CardContent>
                </Card>
            )}
            <Card className="cursor-pointer" onClick={() => setProjectsExpanded(v => !v)}>
                <CardHeader className="flex items-center justify-between cursor-pointer" onClick={(e) => { e.stopPropagation(); setProjectsExpanded(v => !v); }}>
                    <CardTitle>تقارير المشاريع</CardTitle>
                    <div className="flex items-center gap-2">
                        <Badge>{filteredReports.length}</Badge>
                        <button className="button" onClick={(e) => { e.stopPropagation(); setProjectsExpanded(v => !v); }} aria-label={projectsExpanded ? 'طيّ القسم' : 'فتح القسم'}>
                            <span className="button-box">
                                {projectsExpanded ? <ChevronUp size={20} className="button-elem" /> : <ChevronDown size={20} className="button-elem" />}
                                {projectsExpanded ? <ChevronDown size={20} className="button-elem" /> : <ChevronUp size={20} className="button-elem" />}
                            </span>
                        </button>
                    </div>
                </CardHeader>
            </Card>
            {projectsExpanded && (
                filteredReports.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredReports.map(report => (
                            <ProjectCard key={report.id} project={report} />
                        ))}
                    </div>
                ) : (
                    <Card>
                        <CardContent className="pt-6">
                            <EmptyState
                                icon={Briefcase}
                                title={t('noProjectReports')}
                                message={t('noProjectReportsMessage')}
                            />
                        </CardContent>
                    </Card>
                )
            )}

            <Card className="cursor-pointer" onClick={() => setQuotationsExpanded(v => !v)}>
                <CardHeader className="flex items-center justify-between cursor-pointer" onClick={(e) => { e.stopPropagation(); setQuotationsExpanded(v => !v); }}>
                    <CardTitle>عروض السعر</CardTitle>
                    <div className="flex items-center gap-2">
                        <Badge>{savedQuotations.length}</Badge>
                        <button className="button" onClick={(e) => { e.stopPropagation(); setQuotationsExpanded(v => !v); }} aria-label={quotationsExpanded ? 'طيّ القسم' : 'فتح القسم'}>
                            <span className="button-box">
                                {quotationsExpanded ? <ChevronUp size={20} className="button-elem" /> : <ChevronDown size={20} className="button-elem" />}
                                {quotationsExpanded ? <ChevronDown size={20} className="button-elem" /> : <ChevronUp size={20} className="button-elem" />}
                            </span>
                        </button>
                    </div>
                </CardHeader>
                {quotationsExpanded && (
                    <CardContent>
                        {savedQuotations.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {savedQuotations.map(item => (
                                    <Card
                                        key={item.id}
                                        className="relative cursor-pointer group bg-slate-100 dark:bg-slate-800/50"
                                        onClick={() => navigate(`/quotations/${item.id}`)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => { if (e.key === 'Enter') navigate(`/quotations/${item.id}`); }}
                                    >
                                        <CardHeader className="flex items-center justify-between">
                                            <div className="min-w-0">
                                                <CardTitle className="text-base truncate">{item.customer_name}</CardTitle>
                                                <div className="text-xs text-slate-500 truncate">{item.location || '-'}</div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="p-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100"
                                                onClick={(e) => { e.stopPropagation(); deleteQuotationCard(item.id); }}
                                                title="حذف العرض"
                                                icon={<Trash2 size={16} />}
                                            />
                                        </CardHeader>
                                        <CardContent className="space-y-2 text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-500 flex items-center gap-1"><MapPin size={14} /> موقع العميل</span>
                                                <span className="font-semibold truncate max-w-[55%]">{item.location || '-'}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-500 flex items-center gap-1"><Zap size={14} /> عدد الأحصنة (HP)</span>
                                                <span className="font-semibold">{quotationDetails[item.id]?.horsepower ?? '-'}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-500 flex items-center gap-1"><Calendar size={14} /> التاريخ</span>
                                                <span className="font-semibold">{new Date(item.quote_date).toLocaleDateString('en-GB')}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-500">الإجمالي شامل الضريبة</span>
                                                <span className="font-semibold">{Number(item.total_with_tax || 0).toLocaleString('en-GB')} ر.س</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500">لا توجد عروض سعر محفوظة بعد.</div>
                        )}
                    </CardContent>
                )}
            </Card>
        </div>
    );
};

const ProjectDashboardScreen: React.FC = () => {
    const { designMode } = useAppContext();
    return designMode === 'modern' ? <ModernProjectDashboardScreen /> : <ClassicProjectDashboardScreen />;
};

export default ProjectDashboardScreen;
